import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { STAGE_LABEL, STAGES } from '@/lib/roles';
import { useFeature } from '@/lib/features';
import {
  ShieldCheck, ShieldOff, User, Layers, Lock, Plus, CheckCircle2, XCircle, Inbox, ListChecks,
} from 'lucide-react';

export default function AdminGateDelegationsPage() {
  const enabled = useFeature('gate_delegation');
  const [tab, setTab] = useState('active');
  const [delegations, setDelegations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [floors, setFloors] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDecision, setOpenDecision] = useState(null);

  const load = async () => {
    try {
      const [d, r, p, u, f] = await Promise.all([
        api.get('/gate_delegations?active_only=true'),
        api.get('/gate_delegations/requests?status=pending'),
        api.get('/projects'),
        api.get('/users'),
        api.get('/gate_delegations/hard_floors'),
      ]);
      setDelegations(d.data || []);
      setRequests(r.data || []);
      setProjects(p.data || []);
      setUsers(u.data || []);
      setFloors(f.data);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const projMap = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const revoke = async (id) => {
    if (!window.confirm('Revoke this delegation?')) return;
    try { await api.post(`/gate_delegations/${id}/revoke`); toast.success('Revoked.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Admin' }, { label: 'Gate Delegations' }]}>
      <div className="p-6 max-w-[1200px] mx-auto space-y-4 fade-up" data-testid="gate-delegations-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <ShieldCheck size={22} />Gate Authority Delegations
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              Assign a named person to hold gate-passing authority on a specific project stage. Delegations are
              scoped, revocable, and never touch financial or governance permissions (see hard floors below).
            </p>
          </div>
          <Button onClick={() => setOpenCreate(true)} disabled={!enabled} data-testid="gate-delegation-create-cta">
            <Plus size={14} className="mr-1" />New delegation
          </Button>
        </div>

        {floors && (
          <div className="rounded-md border p-3 flex items-start gap-2 bg-[hsl(var(--surface-1))]" style={{ borderColor: 'hsl(var(--border))' }} data-testid="gate-hard-floors">
            <Lock size={15} className="mt-0.5 flex-none text-[hsl(var(--danger))]" />
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <b className="text-[hsl(var(--foreground))]">Hard floors — never delegable via this mechanism:</b>
              <div className="flex flex-wrap gap-1 mt-1">
                {(floors.hard_floors || []).map(f => (
                  <span key={f} className="chip">{f.replace(/_/g, ' ')}</span>
                ))}
              </div>
              <div className="mt-1 italic">{floors.note}</div>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="active" data-testid="gd-tab-active">
              <ListChecks size={13} className="mr-1" />Active delegations ({delegations.length})
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="gd-tab-requests">
              <Inbox size={13} className="mr-1" />Pending requests ({requests.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'active' && (
          <div className="space-y-2">
            {delegations.length === 0 && (
              <div className="text-center py-10 border rounded-lg text-[hsl(var(--foreground-muted))] text-[13.5px]" style={{ borderColor: 'hsl(var(--border))' }}>
                No active delegations. Default gate holders are in effect on every stage.
              </div>
            )}
            {delegations.map(d => (
              <div key={d.id} className="card p-3 flex items-start justify-between gap-3" data-testid={`gd-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip chip-success">Active</span>
                    <span className="font-medium"><Layers size={11} className="inline mr-1" />{projMap[d.project_id]?.name || d.project_id}</span>
                    <span className="text-[12px] text-[hsl(var(--foreground-muted))]">· {STAGE_LABEL[d.stage] || d.stage}</span>
                  </div>
                  <div className="text-[12.5px] mt-1 flex items-center gap-1">
                    <User size={12} />
                    <b>{d.delegate_name || (userMap[d.delegate_user_id]?.name) || d.delegate_user_id}</b>
                    <span className="text-[hsl(var(--foreground-muted))]">holds authority for this gate</span>
                  </div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">
                    Delegated by {d.delegated_by_email} · {new Date(d.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                    {d.reason && <> · <i>&ldquo;{d.reason}&rdquo;</i></>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => revoke(d.id)} data-testid={`gd-revoke-${d.id}`}>
                  <ShieldOff size={13} className="mr-1" />Revoke
                </Button>
              </div>
            ))}
          </div>
        )}

        {tab === 'requests' && (
          <div className="space-y-2">
            {requests.length === 0 && (
              <div className="text-center py-10 border rounded-lg text-[hsl(var(--foreground-muted))] text-[13.5px]" style={{ borderColor: 'hsl(var(--border))' }}>
                No pending authority requests.
              </div>
            )}
            {requests.map(r => (
              <div key={r.id} className="card p-3" data-testid={`gd-req-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="chip chip-warning">Pending</span>
                  <span className="font-medium">{r.requester_name || r.requester_email}</span>
                  <span className="text-[12px] text-[hsl(var(--foreground-muted))]">requests {STAGE_LABEL[r.stage] || r.stage} on</span>
                  <span className="text-[12px] font-medium">{projMap[r.project_id]?.name || r.project_id}</span>
                </div>
                <div className="text-[13px] italic mt-1">&ldquo;{r.reason}&rdquo;</div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setOpenDecision({ req: r, decision: 'deny' })} data-testid={`gd-req-deny-${r.id}`}>
                    <XCircle size={13} className="mr-1" />Deny
                  </Button>
                  <Button size="sm" onClick={() => setOpenDecision({ req: r, decision: 'approve' })} data-testid={`gd-req-approve-${r.id}`}>
                    <CheckCircle2 size={13} className="mr-1" />Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openCreate && (
        <CreateDialog projects={projects} users={users} onClose={() => setOpenCreate(false)}
                      onDone={() => { setOpenCreate(false); load(); }} />
      )}

      {openDecision && (
        <DecisionDialog {...openDecision}
                        onClose={() => setOpenDecision(null)}
                        onDone={() => { setOpenDecision(null); load(); }} />
      )}
    </AppShell>
  );
}

function CreateDialog({ projects, users, onClose, onDone }) {
  const [projectId, setProjectId] = useState('');
  const [stage, setStage] = useState(STAGES[0]?.key || 'strategy_concept');
  const [delegateId, setDelegateId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const canSave = projectId && stage && delegateId;

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/gate_delegations', {
        project_id: projectId, stage, delegate_user_id: delegateId,
        reason: reason.trim() || null,
      });
      toast.success('Delegation created and the delegate has been notified.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="gd-create-dialog">
        <DialogHeader>
          <DialogTitle>New gate delegation</DialogTitle>
          <DialogDescription>Delegate gate-passing authority for one stage of one project. Revoke any time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Project</Label>
            <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                    value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="gd-create-project">
              <option value="">— Select —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Stage</Label>
            <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                    value={stage} onChange={e => setStage(e.target.value)} data-testid="gd-create-stage">
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Delegate</Label>
            <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                    value={delegateId} onChange={e => setDelegateId(e.target.value)} data-testid="gd-create-delegate">
              <option value="">— Select a teammate —</option>
              {users.filter(u => u.status !== 'inactive').map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email} — {u.role?.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="e.g. Rohan is deputising while Neha is on leave for Week 32." data-testid="gd-create-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="gd-create-save">
            {saving ? 'Saving…' : 'Create delegation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ req, decision, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const isApprove = decision === 'approve';
  const canSave = note.trim().length >= 3;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/gate_delegations/request/${req.id}/decide`, {
        decision, note: note.trim(),
      });
      toast.success(isApprove ? 'Approved. A delegation has been created and the requester has been notified.' : 'Denied. Requester notified.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="gd-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isApprove ? 'Approve authority request' : 'Deny authority request'}</DialogTitle>
          <DialogDescription>
            One line back to the requester. Kept in the audit log.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                  placeholder={isApprove ? 'e.g. Approved — you are gate holder for schematic on Aravali until Aug 31.' : 'e.g. Not needed — Neha will be back in office by Monday.'}
                  data-testid="gd-decision-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="gd-decision-confirm"
                  variant={isApprove ? 'default' : 'destructive'}>
            {saving ? 'Saving…' : isApprove ? 'Approve' : 'Deny'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Label = ({ children }) => (
  <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">{children}</div>
);
